import React, { Component } from "react"
import PropTypes from "prop-types"
import isEqual from "lodash.isequal"

import { formatNumberForDisplay } from "shared/imports/sharedHelperFunctions"
import {
    STAT_COLOR_SCALE_START,
    STAT_COLOR_SCALE_END,
    QUORUM_GREY_2, // for states with value = 0
    WHITE, // for text over states
    QUORUM_GREY_4, // for text over oceans
    QUORUM_BLUE,
    HYPER_LINK_PURPLE,
} from "shared/imports/sharedConstants"

export default class GenericMap extends Component {
    static propTypes = {
        isLoading: PropTypes.bool.isRequired,
        updateGraph: PropTypes.func.isRequired,
        data: PropTypes.array,
        selectedRegions: PropTypes.array,
        colorConfigs: PropTypes.object,
        useResourceId: PropTypes.bool,
        // map configs
        geographyData: PropTypes.array.isRequired,
        configs: PropTypes.shape({
            getLabelOffsets: PropTypes.func,
            getMapProjection: PropTypes.func,
            outsideLabels: PropTypes.array,
            hideLabels: PropTypes.bool,
        }),
    }

    static defaultProps = {
        colorConfigs: {
            noValueColor: QUORUM_GREY_2,
            lighterPrimaryColor: STAT_COLOR_SCALE_START,
            darkerPrimaryColor: STAT_COLOR_SCALE_END,
            secondaryColor: HYPER_LINK_PURPLE,
        },
    }

    componentDidMount() {
        this.setupGraph()
    }

    componentDidUpdate(oldProps) {
        const loadingFinished = oldProps.isLoading && !this.props.isLoading
        const colorChange = !isEqual(oldProps.colorConfigs, this.props.colorConfigs)

        if (loadingFinished) {
            this.applyData()
        } else if (oldProps.selectedRegions.length !== this.props.selectedRegions.length || colorChange) {
            this.colorRegions({ colorChanged: colorChange })
        }
    }

    componentWillUnmount() {
        d3.select(".d3-tip").remove()
    }

    setupGraph() {
        // This is how far from the edge of the SVG the legend should be by default
        const defaultOffset = 80

        // If the offset is more than 10% of the whole drawing, we cut it in half and adjust the rest of the svg to fit
        this.shouldCorrect = parseInt(d3.select(this.node).style("width")) / defaultOffset < 10
        this.legendCorrection = this.shouldCorrect ? defaultOffset / 2 : defaultOffset

        //  When the projection scaling below shrinks the map, this will keep the rest of the drawing to scale
        this.svgWidth = parseInt(d3.select(this.node).style("width")) - this.shouldCorrect * this.legendCorrection
        this.svgHeight = parseInt(d3.select(this.node).style("height"))

        const projection = this.props.configs
            // Only the height is taken into consideration when scaling the projection
            .getMapProjection(this.svgWidth, this.svgHeight - this.shouldCorrect * this.legendCorrection)
            .translate([this.svgWidth / 2, this.svgHeight / 2])

        this.path = d3.geo.path().projection(projection)

        this.legendVerticalMargin = 16
        this.legendBarIntervalHeight = (this.svgHeight - this.legendVerticalMargin * 2) / 5

        this.drawGraph()
    }

    getTextCoordinates(datum) {
        const textAdjustment = this.textAdjustments[datum.enumId]
        const centroid = this.path.centroid(datum)
        const containerSize = [this.svgWidth, this.svgHeight]

        if (centroid.some((coor) => isNaN(coor))) {
            return [0, 0]
        }

        // just a fun way to take care of x and y adjustments in a map
        return textAdjustment
            ? textAdjustment.map((adj, idx) => {
                  const offset = !this.props.configs.useProportionalOffset ? adj : containerSize[idx] / adj

                  return centroid[idx] + offset
              })
            : centroid
    }

    drawGraph() {
        this.textAdjustments = this.props.configs.getLabelOffsets(this.svgWidth, this.svgHeight)
        const allData = this.props.geographyData.map((region) => ({
            ...region,
            textCoordinates: this.getTextCoordinates(region),
        }))

        // setup the tooltips! (Technically this deals with data, but we need to attach
        // the mouseenter and mouseleave in the initial drawing of the map, not in the
        // rendering of the data.)
        const tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .offset([0, 0])
            .html((d) => {
                const value = Userdata.isGlobalMode()
                    ? this.getJSONValueFromLabel(d.long_display)
                    : this.getJSONValueFromId(d.enumId).toLocaleString()
                return `${d.long_display}: ${value}`
            })

        d3.select(this.node).call(tooltip)

        const wrapper = d3.select(this.node).append("g").attr("transform", "translate(-25, -3)")

        // draw the states
        wrapper
            .append("g")
            .attr("class", "regions")
            .selectAll("g")
            .data(allData)
            .enter()
            .append("g")
            .attr("class", "region")
            .append("path")
            .attr("d", this.path)
            .attr("id", (d) => `id_${d.enumId}`)
            .style("stroke-width", 1)
            .style("stroke", "white")
            .on("click", (d) => {
                const id = this.props.useResourceId ? d.resourceId : d.enumId

                if (Userdata.isGlobalMode()) {
                    const displayName =
                        d.long_display === "United States"
                            ? DjangIO.app.models.SupportedRegion.federal.region_name
                            : d.long_display

                    const regionByLabel = this.getSupportedRegionByLabel(displayName)

                    regionByLabel && this.props.updateGraph(regionByLabel.value)
                } else {
                    this.props.updateGraph(id)
                }
            })
            .on("mouseenter", function (d) {
                tooltip.show(d)
                d3.select(this).style("opacity", 0.75)
                d3.select(this).style("stroke-width", 3)
            })
            .on("mouseleave", function (d) {
                tooltip.hide(d)
                d3.select(this).style("opacity", 1)
                d3.select(this).style("stroke-width", 1)
            })

        // apply labels (eg "VA", "NE", "TX", etc)
        if (!this.props.configs.hideLabels) {
            d3.select(this.node)
                .selectAll(".region")
                .append("text")
                .text((d) => (!this.props.configs.excludeLabels.includes(d.enumId) ? d.short_display : ""))
                .attr("x", (d) => d.textCoordinates[0])
                .attr("y", (d) => d.textCoordinates[1])
                .attr("text-anchor", "middle")
                .attr("font-size", "0.8em")
                .style("pointer-events", "none")
                .style("fill", (d) => (this.props.configs.outsideLabels.includes(d.enumId) ? QUORUM_GREY_4 : WHITE))
        }

        // create legend group
        d3.select(this.node)
            .append("g")
            .attr("class", "legend")
            // move the legend to the right side of the map, and apply the appropriate legendCorrection
            .attr("transform", `translate(${this.svgWidth - this.legendCorrection}, ${this.legendVerticalMargin})`)
            .selectAll("g")
            .data([0, 1, 2, 3, 4])
            .enter()
            .append("g")
            .append("rect")
            .attr("id", (d) => `legend_${d}`)
            .attr("width", 35)
            .attr("height", this.legendBarIntervalHeight)
            .attr("x", 0)
            .attr("y", (d) => this.legendBarIntervalHeight * d)
            .attr("fill-opacity", 0)

        d3.select(this.node).select(".legend").selectAll("g").append("text")

        this.applyData()
    }

    getJSONValueFromId(regionId) {
        const datum = this.props.data.find((item) => item.id === regionId)
        return datum ? datum.value : 0
    }

    getJSONValueFromLabel(label) {
        if (!this.props.data.length) {
            return 0
        }

        const regionName = label === "United States" ? DjangIO.app.models.SupportedRegion.federal.region_name : label
        const datum = this.props.data.find((item) => item.long_display === regionName)

        return datum ? datum.value : 0
    }

    colorRegions({ colorChanged = false } = { colorChanged: false }) {
        if (colorChanged) {
            this.getColorScale()
            this.applyLegend()
        }

        d3.select(this.node)
            .selectAll(".region")
            .style("fill", (d) => {
                const id = this.props.useResourceId ? d.resourceId : d.enumId
                const value = Userdata.isGlobalMode()
                    ? this.getJSONValueFromLabel(d.long_display)
                    : this.getJSONValueFromId(d.enumId)

                if (Userdata.isGlobalMode()) {
                    const displayName =
                        d.long_display === "United States"
                            ? DjangIO.app.models.SupportedRegion.federal.region_name
                            : d.long_display
                    const regionByLabel = this.getSupportedRegionByLabel(displayName)
                    const colorAsSelected = regionByLabel && this.props.selectedRegions?.includes(regionByLabel.value)

                    if (colorAsSelected) {
                        return this.props.colorConfigs.secondaryColor
                    }
                }

                if (this.props.selectedRegions.includes(id) && !Userdata.isGlobalMode()) {
                    return this.props.colorConfigs.secondaryColor
                } else if (value) {
                    return this.colorScale(value)
                } else {
                    return this.props.colorConfigs.noValueColor
                }
            })
    }

    getSupportedRegionByLabel(label) {
        return DjangIO.app.models.SupportedRegion.items().find(
            (region) => region.region_name.toLowerCase() === label.toLowerCase(),
        )
    }

    getColorScale() {
        this.extent = d3.extent(this.props.data, (d) => (d.state !== "us" ? d.value : 0))

        const MapColorScale = d3.scale
            .linear()
            .interpolate(d3.interpolateHcl)
            .range([this.props.colorConfigs.lighterPrimaryColor, this.props.colorConfigs.darkerPrimaryColor])

        this.colorScale = MapColorScale.domain(this.extent)
    }

    applyLegend() {
        const valueRange = this.extent[1] - this.extent[0]

        d3.select(this.node)
            .select(".legend")
            .selectAll("rect")
            .attr("fill-opacity", 1)
            .style("fill", (d) => this.colorScale(this.extent[0] + d * 0.25 * valueRange))

        d3.select(this.node)
            .select(".legend")
            .selectAll("text")
            .text((d) => formatNumberForDisplay(this.extent[0] + d * 0.25 * valueRange, 0))
            .attr("x", 40)
            .attr("y", (d) => d * this.legendBarIntervalHeight + 40)
            .attr("font-size", "0.8em")
            .style("fill", QUORUM_BLUE)
    }

    applyData() {
        this.getColorScale()
        this.colorRegions()
        this.applyLegend()
    }

    render() {
        return (
            <svg
                ref={(ref) => {
                    this.node = ref
                }}
                id={this.props.svgId}
            />
        )
    }
}
